// Tab "Breakdown": items per categorie met status (klik = volgende status), zoeken en filter.
import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Checkbox, IconButton, InputBase, LinearProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { meldFout } from '../../../utils/apiFout';
import {
  api, dupliceerItem, nieuwItem, perItem, wijzigItem, korteDatum, vandaagIso, CATEGORIEEN, INKT, LIJN, STATUSSEN, STATUS_VOLGORDE, STIL,
} from '../breakdownApi';
import { Kaart, Primair, Secundair, Stil, StatusChip } from '../onderdelen';

function ItemRij({ item, onOpen, onVolgendeStatus, onDupliceer, kiezen, gekozen: aangevinkt, onVink }) {
  const gekozen = item.options.find((o) => o.id === item.selected_option_id);
  const telaat = item.due_date && item.status !== 'bevestigd' && item.due_date < vandaagIso();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderTop: `1px solid ${LIJN}`,
      '&:first-of-type': { borderTop: 0 }, '&:hover': { bgcolor: '#fafbfc' }, '&:hover .dupliceer': { opacity: 1 } }}>
      {kiezen && <Checkbox size="small" checked={aangevinkt} onChange={onVink} inputProps={{ 'aria-label': `${item.name} selecteren` }} sx={{ p: 0.5 }} />}
      <ButtonBase onClick={kiezen ? onVink : onOpen} sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', display: 'block' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: INKT }}>{item.name}</Typography>
          {item.scene_number != null && <Typography sx={{ fontSize: 12, color: STIL, flexShrink: 0 }}>sc. {item.scene_number}</Typography>}
        </Box>
        <Typography noWrap sx={{ fontSize: 12.5, color: STIL }}>
          {[
            gekozen ? `✓ ${gekozen.name}${gekozen.price ? ` · ${gekozen.price}` : ''}` : item.options.length ? `${item.options.length} optie(s)` : null,
            item.owner ? `👤 ${item.owner}` : null,
          ].filter(Boolean).join('  ·  ') || 'Nog niets ingevuld'}
        </Typography>
      </ButtonBase>
      {item.due_date && (
        <Typography sx={{ fontSize: 12, color: telaat ? '#b91c1c' : STIL, fontWeight: telaat ? 700 : 400, flexShrink: 0 }}>
          {korteDatum(item.due_date)}
        </Typography>
      )}
      {!kiezen && (
        <IconButton className="dupliceer" size="small" aria-label={`${item.name} dupliceren`} title="Dupliceren" onClick={onDupliceer}
          sx={{ opacity: { xs: 1, md: 0 }, color: STIL }}>
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      <StatusChip status={item.status} onClick={kiezen ? undefined : onVolgendeStatus} />
    </Box>
  );
}

function CategorieBlok({ categorie, items, toevoegen, onOpen, onVolgendeStatus, onToevoegen, onDupliceer, kiezen, selectie, onVink }) {
  const [naam, setNaam] = useState('');
  const voegToe = () => {
    const n = naam.trim();
    if (!n) return;
    setNaam('');
    onToevoegen(categorie.key, n);
  };
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: INKT }}>{categorie.icoon} {categorie.label}</Typography>
        <Typography sx={{ fontSize: 12, color: STIL }}>{items.length || ''}</Typography>
      </Box>
      <Kaart>
        {items.map((i) => (
          <ItemRij key={i.id} item={i} onOpen={() => onOpen(i.id)} onVolgendeStatus={() => onVolgendeStatus(i)}
            onDupliceer={() => onDupliceer(i)} kiezen={kiezen} gekozen={selectie.has(i.id)} onVink={() => onVink(i.id)} />
        ))}
        {toevoegen && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderTop: items.length ? `1px solid ${LIJN}` : 0 }}>
            <AddIcon sx={{ fontSize: 16, color: STIL }} />
            <InputBase fullWidth value={naam} onChange={(e) => setNaam(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }}
              placeholder={`${categorie.label} toevoegen…`} inputProps={{ 'aria-label': `${categorie.label} toevoegen` }} sx={{ fontSize: 13.5 }} />
          </Box>
        )}
      </Kaart>
    </Box>
  );
}

export default function BreakdownTab({ productie, items, onItem, onItemErbij, onItemWeg, onOpen }) {
  const [statusFilter, setStatusFilter] = useState(null);
  const [zoek, setZoek] = useState('');
  const [kiezen, setKiezen] = useState(false);
  const [selectie, setSelectie] = useState(() => new Set());
  const [bezig, setBezig] = useState(false);

  const telling = useMemo(() => {
    const t = { open: 0, toegezegd: 0, bevestigd: 0 };
    items.forEach((i) => { t[i.status] = (t[i.status] || 0) + 1; });
    return t;
  }, [items]);
  const telaat = items.filter((i) => i.due_date && i.status !== 'bevestigd' && i.due_date < vandaagIso());
  const q = zoek.trim().toLowerCase();
  const zichtbaar = items.filter((i) => (!statusFilter || i.status === statusFilter) && (!q || i.name.toLowerCase().includes(q)));
  const gefilterd = !!statusFilter || !!q;
  const pct = items.length ? Math.round((telling.bevestigd / items.length) * 100) : 0;

  async function voegToe(category, name) {
    try { onItemErbij(await nieuwItem(productie.id, { category, name })); }
    catch (e) { meldFout(e, 'Item toevoegen'); }
  }

  const vink = (id) => setSelectie((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const stopKiezen = () => { setKiezen(false); setSelectie(new Set()); };
  const gekozenItems = items.filter((i) => selectie.has(i.id));

  async function dupliceer(item) {
    try {
      const kopie = await dupliceerItem(item.id);
      onItemErbij(kopie);
      biedOngedaanAan(`"${kopie.name}" gemaakt`, async () => {
        await api(`/items/${kopie.id}`, { method: 'DELETE', wat: 'Kopie verwijderen' });
        onItemWeg(kopie.id, kopie.production_id);
      });
    } catch (e) { meldFout(e, 'Dupliceren'); }
  }

  async function zetStatusVoorSelectie(status) {
    const vorige = gekozenItems.map((i) => [i.id, i.status]).filter(([, s]) => s !== status);
    if (!vorige.length || bezig) return;
    setBezig(true);
    const gelukt = [];
    for (const [id, oud] of vorige) {
      try { onItem(await wijzigItem(id, { status })); gelukt.push([id, oud]); }
      catch (e) { meldFout(e, 'Status opslaan'); }
    }
    setBezig(false);
    stopKiezen();
    if (gelukt.length) {
      biedOngedaanAan(`${gelukt.length} item(s) → ${STATUSSEN[status].label}`, async () => {
        for (const [id, oud] of gelukt) onItem(await wijzigItem(id, { status: oud }));
      });
    }
  }

  async function verwijderSelectie() {
    if (!gekozenItems.length || bezig) return;
    const ok = await bevestig({
      titel: `${gekozenItems.length} item(s) verwijderen?`,
      tekst: `${gekozenItems.slice(0, 5).map((i) => `"${i.name}"`).join(', ')}${gekozenItems.length > 5 ? ` en nog ${gekozenItems.length - 5}` : ''} worden definitief verwijderd, met hun opties en documenten.`,
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    setBezig(true);
    for (const i of gekozenItems) {
      try {
        await perItem(i.id, () => api(`/items/${i.id}`, { method: 'DELETE', wat: 'Item verwijderen' }));
        onItemWeg(i.id, i.production_id);
      } catch (e) { meldFout(e, 'Item verwijderen'); }
    }
    setBezig(false);
    stopKiezen();
  }

  async function volgendeStatus(item) {
    const vorige = item.status;
    const volgende = STATUS_VOLGORDE[(STATUS_VOLGORDE.indexOf(vorige) + 1) % STATUS_VOLGORDE.length];
    try {
      onItem(await wijzigItem(item.id, { status: volgende }));
      biedOngedaanAan(`"${item.name}" → ${STATUSSEN[volgende].label}`, async () => onItem(await wijzigItem(item.id, { status: vorige })));
    } catch (e) { meldFout(e, 'Status opslaan'); }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 160 }}>
          <Typography sx={{ fontSize: 12.5, color: STIL, mb: 0.5 }}>{telling.bevestigd} van {items.length} bevestigd</Typography>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: LIJN, '& .MuiLinearProgress-bar': { bgcolor: '#15803d' } }} />
        </Box>
        {[null, ...STATUS_VOLGORDE].map((s) => {
          const actief = statusFilter === s;
          return (
            <ButtonBase key={s || 'alles'} onClick={() => setStatusFilter(s)} aria-pressed={actief}
              sx={{ px: 1.25, py: 0.5, borderRadius: 10, fontSize: 12.5, fontWeight: actief ? 700 : 500,
                color: actief ? INKT : STIL, bgcolor: actief ? '#eceff3' : 'transparent', border: `1px solid ${actief ? '#d5dae1' : LIJN}` }}>
              {s ? `${STATUSSEN[s].label} (${telling[s]})` : `Alles (${items.length})`}
            </ButtonBase>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: kiezen ? 1 : 2.5 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${LIJN}`, borderRadius: 1.5, px: 1.25 }}>
          <SearchIcon sx={{ fontSize: 18, color: STIL }} />
          <InputBase fullWidth value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek een item…"
            inputProps={{ 'aria-label': 'Zoek een item' }} sx={{ fontSize: 13.5, py: 0.5 }} />
        </Box>
        {items.length > 0 && (kiezen
          ? <Stil onClick={stopKiezen}>Klaar</Stil>
          : <Secundair onClick={() => setKiezen(true)}>Selecteren</Secundair>)}
      </Box>
      {kiezen && (
        <Box sx={{ position: 'sticky', top: 8, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          border: `1px solid ${LIJN}`, bgcolor: '#f8fafc', borderRadius: 2, px: 1.5, py: 1, mb: 2.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, mr: 1 }}>{selectie.size} geselecteerd</Typography>
          <Stil onClick={() => setSelectie(new Set(zichtbaar.map((i) => i.id)))}>Alles zichtbaar</Stil>
          <Box sx={{ flex: 1 }} />
          {STATUS_VOLGORDE.map((s) => (
            <Secundair key={s} disabled={!selectie.size || bezig} onClick={() => zetStatusVoorSelectie(s)}>{STATUSSEN[s].label}</Secundair>
          ))}
          <Primair disabled={!selectie.size || bezig} onClick={verwijderSelectie} sx={{ bgcolor: '#b91c1c', '&:hover': { bgcolor: '#991b1b' } }}>Verwijderen</Primair>
        </Box>
      )}

      {telaat.length > 0 && !gefilterd && (
        <Box sx={{ border: '1px solid #fecaca', bgcolor: '#fef2f2', borderRadius: 2, px: 1.5, py: 1, mb: 2.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#b91c1c' }}>{telaat.length} item(s) over de deadline</Typography>
          {telaat.map((i) => (
            <ButtonBase key={i.id} onClick={() => onOpen(i.id)} sx={{ display: 'block', textAlign: 'left', fontSize: 13, color: '#7f1d1d' }}>
              {i.name} — {korteDatum(i.due_date)}
            </ButtonBase>
          ))}
        </Box>
      )}

      {items.length === 0 && (
        <Typography sx={{ fontSize: 13.5, color: STIL, mb: 2 }}>
          Nog geen items. Typ hieronder per categorie, of laat het tabblad <b>Script</b> een eerste breakdown maken.
        </Typography>
      )}

      {CATEGORIEEN.map((c) => {
        const lijst = zichtbaar.filter((i) => i.category === c.key);
        if (gefilterd && lijst.length === 0) return null;
        return (
          <CategorieBlok key={c.key} categorie={c} items={lijst} toevoegen={!gefilterd && !kiezen}
            onOpen={onOpen} onVolgendeStatus={volgendeStatus} onToevoegen={voegToe} onDupliceer={dupliceer}
            kiezen={kiezen} selectie={selectie} onVink={vink} />
        );
      })}
      {gefilterd && zichtbaar.length === 0 && <Typography sx={{ fontSize: 13.5, color: STIL }}>Geen items gevonden.</Typography>}
    </Box>
  );
}
